import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, Empty } from '../components/ui';
import { initials, composeAddress } from '../lib/format';

const BLANK = { name: '', phone: '', email: '', address1: '', city: '', state: '' };

export default function Customers() {
  const { toast, run } = useApp();
  const { data: customers, error, loading, reload } = useAsync(() => api.get('/customers'), []);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(p => ({ ...p, [k]: e.target.value }));

  // Filtering in state rather than toggling display on DOM nodes, so the
  // result count stays truthful and screen readers see only matching rows.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return customers || [];
    return (customers || []).filter(c =>
      [c.name, c.email, c.phone, composeAddress(c)]
        .filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [customers, q]);

  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      setBusy(true);
      await api.post('/customers', f);
      setF(BLANK); setAdding(false);
      toast('Customer added');
      reload();
      setBusy(false);
    }, { onError: (m) => { setBusy(false); toast(m); } });
  };

  return (
    <>
      <PageHeader
        title="Customers"
        sub={customers ? `${customers.length} record${customers.length === 1 ? '' : 's'}` : ''}
        action={<button className="act" onClick={() => setAdding(a => !a)}>{adding ? 'Cancel' : '＋ Add'}</button>}
      />
      <div className="view">
        {/*
          Adding a customer used to be a chain of window.prompt() dialogs —
          one per field, unlabelled, uncancellable halfway, and invisible to
          assistive tech. A real form instead.
        */}
        {adding ? (
          <Card>
            <SectionTitle>New customer</SectionTitle>
            <form className="col" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-3)' }} onSubmit={submit}>
              <Field label="Name *" id="cname"><input id="cname" className="fi" required value={f.name} onChange={set('name')} /></Field>
              <div className="row-fields">
                <Field label="Phone *" id="cphone"><input id="cphone" className="fi" type="tel" required value={f.phone} onChange={set('phone')} /></Field>
                <Field label="Email" id="cemail"><input id="cemail" className="fi" type="email" value={f.email} onChange={set('email')} /></Field>
              </div>
              <Field label="Address" id="caddr"><input id="caddr" className="fi" value={f.address1} onChange={set('address1')} /></Field>
              <div className="row-fields">
                <Field label="City" id="ccity"><input id="ccity" className="fi" value={f.city} onChange={set('city')} /></Field>
                <Field label="State" id="cstate"><input id="cstate" className="fi" value={f.state} onChange={set('state')} /></Field>
              </div>
              <button className="btn pri" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Add customer'}</button>
            </form>
          </Card>
        ) : null}

        <Field label="Search customers" id="search">
          <input id="search" className="fi" type="search" placeholder="Name, phone, email or address…"
            value={q} onChange={e => setQ(e.target.value)} />
        </Field>

        {loading ? <Spinner label="Loading customers" /> : null}
        {error ? <ErrorNote onRetry={reload}>{error}</ErrorNote> : null}

        {customers ? (
          <Card tight>
            {shown.length ? shown.map(c => (
              <Link key={c.id} className="listrow" to={`/customer/${c.id}`}>
                <span className="ava" aria-hidden="true">{initials(c.name)}</span>
                <span className="nm">
                  {c.name}
                  <span className="sb" style={{ display: 'block', fontWeight: 400 }}>
                    {composeAddress(c) || c.email || '—'} · {c.projectCount} project{c.projectCount === 1 ? '' : 's'}
                  </span>
                </span>
                <span className="sb" aria-hidden="true">›</span>
              </Link>
            )) : <Empty>{q ? `No customers match “${q}”.` : 'No customers yet.'}</Empty>}
          </Card>
        ) : null}
      </div>
    </>
  );
}
